import { useState, type ReactNode } from "react";
import { COLORS } from "../../lib/brand";
import { Platform, Pressable, View, type ViewStyle } from "react-native";

const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";

/**
 * Card — wraps content in a rounded bordered surface.
 *
 * When `onPress` is set the card becomes pressable and gains:
 *   - Web hover: amber-tinted border + soft amber shadow + 1px lift
 *   - Press: gentle scale-down for tactile feedback
 *   - All transitions 180ms — premium without feeling slow
 */
export function Card({
  variant = "light",
  padding = "md",
  children,
  onPress,
}: {
  variant?: "light" | "dark";
  padding?: "sm" | "md" | "lg";
  children: ReactNode;
  onPress?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const padCls = padding === "sm" ? "p-3.5" : padding === "lg" ? "p-5" : "p-[18px]";
  const baseCls =
    variant === "dark"
      ? "bg-off-black border-white/10"
      : "bg-white border-sand-dark";

  if (onPress) {
    const web = Platform.OS === "web";
    const isHover = web && hovered;
    const hoverStyle: ViewStyle | undefined = isHover
      ? ({
          borderColor: variant === "dark" ? "rgba(181,135,58,0.45)" : AMBER,
          transform: [{ translateY: -1 }],
          ...(web
            ? ({
                boxShadow:
                  variant === "dark"
                    ? "0 14px 28px -16px rgba(181,135,58,0.30)"
                    : "0 14px 28px -16px rgba(28,43,26,0.20)",
              } as unknown as ViewStyle)
            : {}),
        } as ViewStyle)
      : undefined;

    return (
      <Pressable
        onPress={onPress}
        {...(web
          ? ({ onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) } as const)
          : {})}
        className={`rounded-card border ${baseCls} ${padCls}`}
        style={[
          web
            ? ({
                cursor: "pointer",
                transition: "border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
              } as unknown as ViewStyle)
            : null,
          hoverStyle,
        ]}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <View className={`rounded-card border ${baseCls} ${padCls} ${variant === "light" ? "shadow-card-light" : ""}`}>
      {children}
    </View>
  );
}

// Re-export so existing imports keep working.
export { SAND_DARK };
